import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import miningImg from '../assets/sector-mining.jpg';
import commoditiesImg from '../assets/sector-commodities.jpg';
import realEstateImg from '../assets/sector-real-estate.jpg';

export const SectorsSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  const sectors = [
    {
      title: "Natural Resources & Mining",
      image: miningImg,
      features: [
        "Pre-feasibility & bankable feasibility studies",
        "Gold, metals, and oil & gas projects",
        "Investor sourcing & export credit support",
        "Operational & technical optimization"
      ]
    },
    {
      title: "Commodities",
      image: commoditiesImg,
      features: [
        "Iron ore, copper, oil & gas, agricultural",
        "Supply-chain solutions",
        "Off-taker sourcing",
        "End-to-end commercial support"
      ]
    },
    {
      title: "Hospitality & Real Estate",
      image: realEstateImg,
      features: [
        "Asset acquisition & repositioning",
        "Structuring of large-scale developments",
        "Strategic advisory for hotel projects",
        "Mixed-use project development"
      ]
    }
  ];

  return (
    <section id="sectors" ref={containerRef} className="py-24 bg-zinc-900 text-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <motion.h4 
            className="text-amber-500 font-bold uppercase tracking-widest text-sm mb-2"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            Industries
          </motion.h4>
          <motion.h2 
            className="text-3xl md:text-4xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Main Sectors We Serve
          </motion.h2>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {sectors.map((sector, index) => (
            <motion.div
              key={index}
              className="group relative overflow-hidden rounded-sm bg-zinc-800"
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
            >
              {/* Image Background */}
              <div className="h-64 overflow-hidden relative">
                <div className="absolute inset-0 bg-zinc-900/40 group-hover:bg-zinc-900/20 transition-colors duration-500 z-10" />
                <img 
                  src={sector.image} 
                  alt={sector.title} 
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />
              </div>

              {/* Content */}
              <div className="p-8 relative z-20 -mt-12 mx-4 bg-zinc-800 shadow-xl border-t-4 border-amber-500">
                <h3 className="text-xl font-bold mb-6 text-white">{sector.title}</h3>
                <ul className="space-y-3">
                  {sector.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start text-zinc-300 text-sm">
                      <span className="mr-2 text-amber-500 mt-1">•</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};